import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { $countryStore, $recipientCreateStore, $recipientMandatoryFieldsStore } from '@lib/store';
import {
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferUpdateStatusStore,
} from '@lib/store/transferRequests';
import { $transferRequestsUpdateStore } from '@lib/store/transferRequests/transferRequestsUpdateStore';
import { $recipientUpdateStore } from '@lib/store/recipients/recipientUpdateStore';
import { AmountUtils } from '@shared/utils';
import type { ApiResponse } from '@shared/api/models';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { type RecipientResponse, TransferRecipientAccountTypeEnum, TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { TransferRequestStatusEnum, TransferRequestTypeEnum, type TransferRequestResponse } from '@shared/api/rest/transferRequests';
import { RecipientSelectBlock } from '../widgets/select';
import { TransferConfirmationBlock } from '../widgets/transferConfirmation';
import { Button } from '@shared/widgets';
import {
  AccountFormBlock,
  AccountFormDataType,
  AddressFormBlock,
  AddressFormDataType,
  CompanyFormBlock,
  CompanyFormDataType,
  PaymentFormBlock,
  PaymentFormDataType,
} from '../formItem';

export interface IFormDataBusinessOther {
  bankCountry?: string;
  currency: string;
  accountNumber: string;
  accountType?: TransferRecipientAccountTypeEnum;
  bankName: string;
  iban: string;
  bic: string;
  companyName: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  companyCountry: string;
  bankProvince?: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  province?: string;
  amount?: number;
  beneficiaryAmount?: number;
  paymentReason: string;
  purpose: string;
  fastPayment: boolean;
}

interface IProps {
  account: AccountResponse | null;
  allTabs: { label: string; value: TransferRecipientTypeEnum }[];
  currentTab: TransferRecipientTypeEnum;
  onChangeTab: (x: TransferRecipientTypeEnum) => void;
}

export const BusinessTab = (props: IProps) => {
  const fxRateStore = useUnit($transferRequestsGetFxRateStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const countryStore = useUnit($countryStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);

  // const bankInformationStore = useUnit($bankInformationStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);

  const [recipient, setRecipient] = useState<RecipientResponse | null>(null);
  const [isShowRecipientDetails, setIsShowRecipientDetails] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [error, setStateError] = useState<string>('');
  const [otpModal, setOTPModal] = useState<TransferRequestResponse | null>(null);
  const [createdRecipientData, setCreatedRecipientData] = useState<RecipientResponse | null>(null);
  const [createdTransferRequest, setCreatedTransferRequest] = useState<ApiResponse<TransferRequestResponse>>();
  const [recipientId, setRecipientId] = useState<ApiResponse<RecipientResponse>>();

  const formControl = useForm({
    mode: 'onChange',
    defaultValues: {
      bankCountry: undefined,
      currency: 'EUR',
      accountNumber: '',
      accountType: undefined,
      iban: '',
      bankName: '',
      bic: '',
      companyName: '',
      email: '',
      address: '',
      postCode: '',
      city: '',
      companyCountry: '',
      bankProvince: undefined,
      bankAddress: '',
      bankCity: '',
      branchCode: '',
      province: undefined,
      amount: undefined,
      beneficiaryAmount: undefined,
      paymentReason: '',
      fastPayment: false,
    } as IFormDataBusinessOther,
  });
  const reason = formControl.watch('paymentReason');
  const amountVal = formControl.watch('amount');
  const currency = formControl.watch('currency');
  // const bic = formControl.watch('bic');
  const companyCountry = formControl.watch('companyCountry');
  const bankCountry = formControl.watch('bankCountry');
  const totalAmount = (amountVal || 0) + (feeStore.data || 0);

  useEffect(() => {
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (!recipient) return;
    setIsShowRecipientDetails(true);
    setStateError('');
  }, [recipient]);

  useEffect(() => {
    handleRequestFee();
  }, [props.account?.id]);

  const openModal = async () => {
    formControl.handleSubmit(onSubmit)();
  };
  const openConfirmModal = () => {
    setIsShowModal(false);
    setConfirmModal(true);
  };

  const onSubmit = async (data: IFormDataBusinessOther) => {
    try {
      let recipientTmp = recipient;
      if (recipient == null) {
        const request = recipientId === undefined ? await onCreateRecipient(data) : await onUpdateRecipient(data);
        recipientTmp = (request as unknown as { data: RecipientResponse | null }).data;
      }
      if (!recipientTmp || !props.account) return;
      setCreatedRecipientData(recipientTmp);

      if (createdTransferRequest?.data) {
        const updatedTransferRequest = await $transferRequestsUpdateStore.events.update({
          id: transferRequestsCreateStore.data?.id,
          type: TransferRequestTypeEnum.OtherBankTransfer,
          fromAccountId: props.account.id,
          beneficiaryCountryCode: recipientTmp.countryCode,
          beneficiaryCurrency: recipientTmp.currency,
          recipientId: recipientTmp.id,
          senderAmount: data.amount,
          totalAmount: data.amount,
          beneficiaryAmount: data.beneficiaryAmount,
          reasonOfThePayment: data.paymentReason,
          purpose: data.purpose,
          fastPayment: data.fastPayment,
        });

        setCreatedTransferRequest(updatedTransferRequest);
        setIsShowModal(true);
        return;
      }

      const newTransferRequest = await $transferRequestsCreateStore.events.create({
        type: TransferRequestTypeEnum.OtherBankTransfer,
        fromAccountId: props.account.id,
        beneficiaryCountryCode: recipientTmp.countryCode,
        beneficiaryCurrency: recipientTmp.currency,
        recipientId: recipientTmp.id,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        purpose: data.purpose,
        fastPayment: data.fastPayment,
      });

      setCreatedTransferRequest(newTransferRequest);
      setIsShowModal(true);
    } catch {
      if (createdTransferRequest == null) setStateError('error');
    }
  };

  useEffect(() => {
    handleRequestFee();
  }, [recipient, currency, bankCountry]);

  const handleRequestFee = async () => {
    if (amountVal && (recipient?.bankCountryCode || bankCountry)) {
      await $transferRequestsFeeStore.events.update({
        AccountId: props.account?.id,
        SenderAmount: amountVal,
        FastPayment: formControl.watch('fastPayment'),
        BeneficiaryCurrencyCode: recipient?.currency || currency,
        BeneficiaryBankCountryCode: recipient?.bankCountryCode || bankCountry,
        TransferRequestType: TransferRequestTypeEnum.OtherBankTransfer,
      });
    }
  };

  const onDocumentsConfirm = async () => {
    if (transferRequestsCreateStore.data) {
      if (transferRequestsCreateStore.data?.id) {
        try {
          await $transferUpdateStatusStore.events.update({
            id: transferRequestsCreateStore.data?.id,
            status: TransferRequestStatusEnum.WaitingForApproval,
          });
          openConfirmModal();
        } catch {
          setStateError('error');
          openConfirmModal();
        }
      }
    }
  };

  const onCreateRecipient = async (data: IFormDataBusinessOther) => {
    if (recipient != null || currency == null || data.accountType == null) return null;

    const response = await $recipientCreateStore.events.create({
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      currency: currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
    setRecipientId(response);
    return response;
  };
  const onUpdateRecipient = async (data: IFormDataBusinessOther) => {
    const update = await $recipientUpdateStore.events.update({
      id: recipientId?.data?.id,
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      currency: currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
    return update;
  };

  const countryOptions = useMemo(() => countryStore.data.map((x) => ({ label: x.name, value: x.twoLetterCode })), [countryStore]);

  const reasonOptions = useMemo(() => {
    const bankCountryCode = (recipient ? recipient.bankCountryCode : bankCountry)?.toUpperCase();
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data, bankCountry, recipient]);

  // const handleBicCheck = () => {
  //   if (!bankInformationStore.data) return;
  //   setIsInsigniaAccount(bankInformationStore.data.bic === bic.trim());
  // };

  useEffect(() => {
    formControl.setValue('province', undefined);
  }, [companyCountry, formControl.setValue]);
  useEffect(() => {
    formControl.setValue('bankProvince', undefined);
  }, [bankCountry, formControl.setValue]);

  const BankCountryName = countryOptions.find((option) => option.value === bankCountry);
  const ResidenceCountryName = countryOptions.find((option) => option.value === companyCountry);

  useEffect(() => {
    if (BankCountryName && ResidenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.get({
        ResidenceCountryCode: ResidenceCountryName?.value,
        BankCountryCode: BankCountryName?.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Business,
      });
    }
  }, [BankCountryName, ResidenceCountryName, currency]);

  useEffect(() => {
    if (props.account?.id && props.account?.currency && (recipient?.currency || currency) && (bankCountry || recipient?.bankCountryCode))
      $transferRequestsGetFxRateStore.events.update({
        AccountId: props.account?.id,
        FromCurrency: props.account?.currency,
        ToCurrency: recipient?.currency || currency,
        CountryCode: bankCountry || recipient?.bankCountryCode,
      });
  }, [props.account, currency, bankCountry, formControl.formState.isValid, recipient]);

  return (
    <>
      <div className='flex flex-col w-full'>
        <form onSubmit={formControl.handleSubmit(onSubmit)}>
          <div className='dark:bg-soft-black bg-white mt-6 p-6'>
            <RecipientSelectBlock
              recipient={recipient}
              allTabs={props.allTabs}
              currentTab={props.currentTab}
              onChangeTab={props.onChangeTab}
              setRecipient={(recipient) => {
                setRecipient(recipient);
                formControl.setValue('purpose', '');
              }}
              toggleRecipientDetails={() => setIsShowRecipientDetails((prev) => !prev)}
              isShowRecipientDetails={isShowRecipientDetails}
              type={TransferRecipientTypeEnum.Business}
              isInternalAccount={false}
            />
            {!recipient && (
              <>
                <AccountFormBlock
                  formControl={formControl as UseFormReturn<AccountFormDataType, undefined>}
                  countryOptions={countryOptions}
                  countryStore={countryStore.data}
                />
                <CompanyFormBlock formControl={formControl as unknown as UseFormReturn<CompanyFormDataType, undefined>} />
                <AddressFormBlock formControl={formControl as UseFormReturn<AddressFormDataType, undefined>} countryOptions={countryOptions} />
              </>
            )}
          </div>
          <PaymentFormBlock
            formControl={formControl as UseFormReturn<PaymentFormDataType, undefined>}
            handleRequestFee={handleRequestFee}
            feeData={feeStore.data}
            feeLoading={feeStore.loading}
            amount={amountVal}
            totalAmount={totalAmount}
            currency={currency}
            recipient={recipient}
            reasonOptions={reasonOptions}
            account={props.account}
            exchangeRate={fxRateStore}
          />
        </form>
        <div className='flex'>
          <Button
            color='primary'
            className='px-28 mt-3 max-w-72'
            disabled={feeStore.data === undefined || feeStore.loading || transferRequestsCreateStore.loading}
            onClick={openModal}
          >
            Transfer {amountVal && ` ${AmountUtils.transformToAmountWithCurrency(totalAmount, props.account ? props.account.currency : currency)}`}
          </Button>
          {/* <button className="flex justify-center ml-3 border border-black p-2 w-10 h-10">
              <CalendarIcon />
            </button> */}
        </div>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          modal={isShowModal}
          error={error}
          account={props.account!}
          recipient={createdRecipientData}
          transactionId={transferRequestsCreateStore.data?.id}
          amountVal={amountVal}
          fee={feeStore.data || 0}
          reason={reason}
          purpose={reasonOptions.find((reason) => reason.value === formControl.watch('purpose'))?.label || ''}
          exchangeRate={fxRateStore.data!}
          isFastPayment={formControl.watch('fastPayment')}
          closeModal={() => setIsShowModal(false)}
          onDocumentsConfirm={onDocumentsConfirm}
          closeOTPModal={() => setOTPModal(null)}
          otpModal={otpModal}
          openConfirmModal={openConfirmModal}
          confirmModal={confirmModal}
          transferRequest={createdTransferRequest}
        />
      )}
    </>
  );
};
